export const isPdfDocFile = (type = '') => {
  if (type?.includes('pdf') || type?.includes('text')) {
    return true;
  }

  return false;
};

export const isWordExcelFile = (type = '') => {
  if (type?.includes('doc') || type?.includes('docx') || type?.includes('xlsx')) {
    return true;
  }

  return false;
};

export const isImageFile = (type = '') => {
  if (type?.includes('image')) {
    return true;
  }

  return false;
};

export const isPptFile = (type = '') => {
  if (type?.includes('ppt') || type?.includes('pptx')) {
    return true;
  }

  return false;
};

export const isValidDocOrImage = (type = '') => {
  const isValidPdfDoc = isPdfDocFile(type);

  const isValidWordExcel = isWordExcelFile(type);

  const isValidPpt = isPptFile(type);

  const isValidImage = isImageFile(type);

  return isValidPdfDoc || isValidWordExcel || isValidPpt || isValidImage;
};

export const getSearchParamsObj = searchParams => Object.fromEntries(searchParams);

export const getStatusColor = status => {
  switch (status) {
    case 'cancelled':
      return 'error';
    case 'scheduled':
      return 'info';
    case 'completed':
    case 'paid':
    case 'pending':
      return 'success';
    case 'waiting_for_refund':
      return 'seeGreenColor';
    case 'refunded':
      return 'warning';
    case 'in_review':
      return 'purpleColor';
    default:
      return 'default';
  }
};

/**
 * Downloads a file from a given URL.
 * Tries to fetch as blob (for same-origin or CORS-enabled), otherwise falls back to <a> tag.
 * @param {string} url - The file URL.
 * @param {string} filename - The desired filename.
 */
export async function downloadFile(url, filename) {
  try {
    // Try to fetch as blob (works if CORS is enabled)
    const response = await fetch(url, {
      mode: 'cors',
      headers: {
        Accept: 'application/json',
      },
    });
    if (!response.ok) throw new Error('Network response was not ok');
    const blob = await response.blob();

    // Create a blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    // Fallback: open in new tab (browser will handle download or display)
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

export const isTaskVaultRoute = pathname => pathname === '/' || pathname === '/requests' || pathname.startsWith('/requests/');

export const isTaskVaultProjectRoute = (pathname, projectId) => {
  if (!projectId) {
    return false;
  }

  return pathname === `/requests/${projectId}` || pathname.startsWith(`/requests/${projectId}/`);
};

export const isRequestPlusRoute = pathname => pathname === '/client-requests' || pathname.startsWith('/client-requests/');

export const isRequestPlusDetailRoute = (pathname, projectId) => {
  if (!projectId) {
    return false;
  }

  return pathname === `/client-requests/${projectId}` || pathname.startsWith(`/client-requests/${projectId}/`);
};

export const isRequestPlusListRoute = pathname => pathname === '/client-requests' || pathname === '/client-requests/';

export const getClientRequestListQueryArgs = (searchString = '') => {
  const params = new URLSearchParams(searchString);

  return {
    page: Number(params.get('page')) || 1,
    search: params.get('search') || '',
  };
};

export const isDocuSyncListRoute = pathname => pathname === '/docu-sync';

export const isDocuSyncRoute = pathname => pathname === '/docu-sync' || pathname.startsWith('/docu-sync/');

export const isDocuSyncFolderRoute = (pathname, folderId) => {
  if (!folderId) {
    return false;
  }

  return pathname === `/docu-sync/${folderId}` || pathname.startsWith(`/docu-sync/${folderId}/`);
};

export const isContactsRoute = pathname => pathname === '/contacts' || pathname.startsWith('/contacts/');

export const isContactsListRoute = pathname => pathname === '/contacts' || pathname === '/contacts/';

export const isTransactionsRoute = pathname => pathname === '/transactions' || pathname.startsWith('/transactions/');

export const isTransactionsListRoute = pathname => pathname === '/transactions' || pathname === '/transactions/';

export const getTransactionListQueryArgs = search => {
  const searchParams = new URLSearchParams(search);

  return {
    page: Number(searchParams.get('page')) || 1,
    search: searchParams.get('search') || '',
  };
};

export const isTransactionalQuestionsRoute = pathname => pathname === '/transactional-questions' || pathname.startsWith('/transactional-questions/');

export const isPendingAccessRequestsRoute = pathname => pathname === '/pending-access-requests' || pathname.startsWith('/pending-access-requests/');

export const isPendingAccessRequestsListRoute = pathname => pathname === '/pending-access-requests' || pathname === '/pending-access-requests/';

export const isDocumentsRoute = pathname => pathname === '/documents' || pathname.startsWith('/documents/');

export function isTransactionalQuestionGroupUnread(group, unreadQuestionIds = []) {
  const unreadIds = unreadQuestionIds.map(Number);

  return (group?.items || []).some(item => unreadIds.includes(Number(item.id)));
}

export function isDocumentItemUnread(item, unreadDocumentItems = []) {
  return unreadDocumentItems.some(un => Number(un.id) === Number(item.id) && un.type === item.type);
}
